/**
 * Almacenamiento durable para las fotos EN VIVO de una inspección.
 *
 * POR QUÉ EXISTE. `takePictureAsync` escribe en el directorio de CACHÉ, que el
 * sistema puede vaciar cuando le falta espacio — y quince fotos a resolución de
 * cámara son justo la carga que provoca esa limpieza. La pantalla de entrega
 * guardaba esas URIs solo en memoria, así que había dos formas distintas de
 * perder la evidencia de un traspaso: que muriera la app, y que el sistema
 * borrara los archivos. Persistir la URI sin mover el archivo arregla una sola
 * de las dos y deja una promesa apuntando a la nada.
 *
 * ES EL MISMO PROBLEMA, Y LA MISMA SOLUCIÓN, QUE EL ASISTENTE DE PUBLICACIÓN:
 * `lib/utils/listing-photo-storage.ts` ya lo resolvió para las 17 fotos del
 * vehículo. Este archivo es su gemelo para las 15 de la inspección, con la misma
 * forma a propósito — dos mecanismos distintos para un mismo problema es cómo se
 * termina arreglando uno y olvidando el otro.
 *
 * LA PROCEDENCIA NO CAMBIA. La evidencia tiene que ser capturada en vivo y lo
 * sigue siendo: esto corre DESPUÉS de que la cámara devolvió la foto, mueve los
 * mismos bytes, y no toca `source`, `capturedAt` ni las coordenadas. Durabilidad
 * es no perder una foto en vivo, nunca aceptar una de la galería.
 *
 * TODO FALLA ABIERTO: si el movimiento no se puede hacer se devuelve la URI
 * original, porque una foto en un lugar frágil es mejor que ninguna foto.
 */
import { Platform } from 'react-native'
import { Directory, File, Paths } from 'expo-file-system'

/** Subdirectorio del directorio de documentos con las capturas de inspección. */
const INSPECTION_PHOTO_DIR = 'inspection-photos'

function inspectionPhotoDirectory(): Directory {
  const dir = new Directory(Paths.document, INSPECTION_PHOTO_DIR)
  if (!dir.exists) dir.create({ idempotent: true })
  return dir
}

/** True si la URI ya vive en nuestro directorio durable. */
export function isDurableInspectionPhotoUri(uri: string): boolean {
  return uri.includes(`/${INSPECTION_PHOTO_DIR}/`)
}

/**
 * Mueve una captura recién tomada de la caché al almacenamiento durable.
 *
 * Devuelve la URI durable, o la original si el movimiento es imposible (web,
 * archivo ausente, sin espacio). Nunca lanza.
 */
export function persistInspectionPhoto(uri: string, area: string): string {
  if (Platform.OS === 'web') return uri
  if (!uri) return uri
  if (isDurableInspectionPhotoUri(uri)) return uri
  try {
    const origen = new File(uri)
    const destino = new File(inspectionPhotoDirectory(), `${area}-${origen.name}`)
    if (destino.exists) destino.delete()
    origen.move(destino)
    return destino.uri
  } catch {
    return uri
  }
}

/**
 * Borra las capturas durables que ya nadie referencia.
 *
 * Se llama al cerrar sesión, para que el borrador de una cuenta no deje quince
 * fotos del carro de otra persona en el teléfono, inalcanzables desde cualquier
 * pantalla y pertenecientes a alguien que la app ya no conoce. Silencioso al
 * fallar: unos bytes de sobra son una molestia de almacenamiento, nunca un error
 * que mostrarle a nadie.
 */
export function pruneInspectionPhotoFiles(keepUris: readonly string[]): void {
  if (Platform.OS === 'web') return
  try {
    const keep = new Set(keepUris)
    const dir = new Directory(Paths.document, INSPECTION_PHOTO_DIR)
    if (!dir.exists) return
    for (const entry of dir.list()) {
      if (entry instanceof File && !keep.has(entry.uri)) entry.delete()
    }
  } catch {
    // Limpieza de mejor esfuerzo.
  }
}
